import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCubes } from "@fortawesome/free-solid-svg-icons";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { fallbackGradient, lessonIconDefinition } from "./lessonIcons";
import { isAdvancedLessonMode, lessonEpicVisualStyle } from "./lessonAdvancedVisual";

export interface LessonIconProps {
  readonly lessonId: string;
  readonly title: string;
  readonly iconName?: string | null;
  readonly bgColor?: string | null;
  readonly fgColor?: string | null;
  readonly workspaceMode?: LessonWorkspaceMode | null;
  /** Tile size in rem; default 2.75. */
  readonly size?: number;
  readonly className?: string;
}

/**
 * Lesson tile: Font Awesome icon over a solid color, or a deterministic
 * gradient + title initial when no icon/colors are set.
 * Advanced lessons get an epic gradient frame and glowing border.
 */
export function LessonIcon({
  lessonId,
  title,
  iconName,
  bgColor,
  fgColor,
  workspaceMode,
  size = 2.75,
  className,
}: LessonIconProps) {
  const icon = lessonIconDefinition(iconName);
  const epic = lessonEpicVisualStyle({ workspaceMode, baseColor: bgColor, fallbackSeed: lessonId });
  const background =
    epic?.innerBackground ??
    (bgColor?.trim() || (icon ? "#1b4d2e" : fallbackGradient(lessonId)));
  const foreground = fgColor?.trim() || "#fdf9f3";
  const initial = title.trim().charAt(0).toUpperCase() || "?";
  const advanced = isAdvancedLessonMode(workspaceMode);

  const tile = (
    <span
      className={[
        "lesson-icon-tile",
        advanced ? "lesson-icon-tile--advanced" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        width: `${size}rem`,
        height: `${size}rem`,
        background,
        color: foreground,
        fontSize: `${size * 0.45}rem`,
      }}
      aria-hidden
    >
      {icon ? <FontAwesomeIcon icon={icon} /> : <span className="lesson-icon-initial">{initial}</span>}
      {advanced ? (
        <span className="lesson-icon-epic-mark" aria-hidden>
          <FontAwesomeIcon icon={faCubes} />
        </span>
      ) : null}
    </span>
  );

  if (!epic) return tile;

  return (
    <span
      className={epic.frameClassName}
      style={epic.frameStyle}
      title="Advanced · multi-file workspace"
      aria-hidden
    >
      {tile}
    </span>
  );
}
